import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import "./CSS/EntradaMercancias.css";
import { dominio } from "../../../routes_backend/routes.jsx";
import logo2 from "../../../assets/img/ImpresionBodega.png";

const API_URL_ORDENES = `${dominio}/SGIM_OrdenesCompraFilterProveedores`;
const API_URL_DETALLE = `${dominio}/SGIM_OrdenCompra`;
const API_URL_CREAR_ENTRADA = `${dominio}/SGIM_EntradaMercancia`;
const API_URL_ALMACENES = `${dominio}/SGIM_ListaAlmacenes`;
const API_URL_ENTRADAS_MERCANCIA_CANCELACION = `${dominio}/SGIM_EntradaMercanciaFilterProveedores`;
const API_URL_DETALLE_ENTRADA_MERCANCIA = `${dominio}/SGIM_EntradaMercancia`;
const API_URL_ENTRADAS_MERCANCIA_DEVOLUCION = `${dominio}/SGIM_EntradaMercanciaFilterProveedores`;
const API_URL_CREAR_DEVOLUCION_MERCANCIA = `${dominio}/SGIM_DevolucionMercancia`;

const API_KEY = "b4ed47a3-b372-49df-a200-2e68d5f7a454";

const LOGO_TICKET = logo2;
const MAX_DIFERENCIA_TOTAL = 1;
const DEFAULT_IVA_PERCENT = 12;

const formatDateForInput = (dateValue) => {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().split("T")[0];
};

const getTodayForInput = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateDisplay = (dateValue) => {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("es-GT");
};

const escapeHtml = (text) => {
  return String(text ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
};

const round2 = (value) =>
  Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

const round4 = (value) =>
  Math.round((Number(value || 0) + Number.EPSILON) * 10000) / 10000;

const formatMoney2 = (value) =>
  round2(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatMoney4 = (value) =>
  round4(value).toLocaleString("en-US", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });

const sanitizeDecimalInput = (value) => {
  if (value == null) return "";
  let normalized = String(value).replaceAll(",", "").replace(/[^0-9.]/g, "");

  const firstDotIndex = normalized.indexOf(".");
  if (firstDotIndex !== -1) {
    normalized =
      normalized.slice(0, firstDotIndex + 1) +
      normalized.slice(firstDotIndex + 1).replaceAll(".", "");
  }

  return normalized;
};

const parseDecimal = (value) => {
  if (value == null) return 0;
  const normalized = String(value).replaceAll(",", "").trim();
  if (!normalized || normalized === ".") return 0;
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getEditableDecimalValue = (linea, field) => {
  const inputField = `${field}Input`;

  if (linea?.[inputField] !== undefined && linea?.[inputField] !== null) {
    return String(linea[inputField]);
  }

  const value = linea?.[field];

  if (value === undefined || value === null) return "";

  return String(value);
};

const obtenerCantidadPendiente = (linea) => {
  if (linea?.OpenQuantity != null) return Number(linea.OpenQuantity);
  if (linea?.RemainingOpenQuantity != null) return Number(linea.RemainingOpenQuantity);
  if (linea?.Quantity != null) return Number(linea.Quantity);
  return 0;
};

const lineaEstaCerrada = (linea) => {
  return Number(obtenerCantidadPendiente(linea)) <= 0;
};

const obtenerPorcentajeIVA = (linea) => {
  const iva =
    linea?.VatPercent ??
    linea?.VatPrcnt ??
    linea?.TaxPercent ??
    linea?.U_TaxPercent ??
    DEFAULT_IVA_PERCENT;

  const parsed = Number(iva);
  return Number.isNaN(parsed) ? DEFAULT_IVA_PERCENT : parsed;
};

const obtenerFactorIVA = (linea) => {
  return 1 + obtenerPorcentajeIVA(linea) / 100;
};

const obtenerNumeroSAP = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }

  return 0;
};

const obtenerNumeroSAPNullable = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }

  return null;
};

const obtenerFactorDocumentoLinea = (linea) => {
  const lineTotalOriginal = obtenerNumeroSAP(
    linea?.LineTotalSAP,
    linea?.LineTotal,
    linea?.RowTotal,
    linea?.TotalLinea
  );

  const vatSumOriginal = obtenerNumeroSAPNullable(
    linea?.VatSumSAP,
    linea?.VatSum,
    linea?.TaxTotal,
    linea?.TaxAmount,
    linea?.ImpuestoLinea
  );

  if (lineTotalOriginal > 0 && vatSumOriginal !== null) {
    return round6Safe((lineTotalOriginal + vatSumOriginal) / lineTotalOriginal);
  }

  const precioInfo = obtenerNumeroSAP(
    linea?.PrecioInfo,
    linea?.Price,
    linea?.UnitPrice,
    linea?.PriceBefDi
  );

  const precioConIVAOriginal = obtenerNumeroSAP(
    linea?.PriceAfterVAT,
    linea?.GrossPrice,
    linea?.PrecioConIVAOriginal
  );

  if (precioInfo > 0 && precioConIVAOriginal > 0) {
    return round6Safe(precioConIVAOriginal / precioInfo);
  }

  return obtenerFactorIVA(linea);
};

const round6Safe = (value) => {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed) || parsed <= 0) return 1;
  return Math.round((parsed + Number.EPSILON) * 1000000) / 1000000;
};

const obtenerPrecioInfoSAP = (linea) => {
  return obtenerNumeroSAP(
    linea?.PrecioInfo,
    linea?.Price,
    linea?.UnitPrice,
    linea?.PriceBefDi
  );
};

const obtenerPrecioConIVASAP = (linea) => {
  const precioSAP = obtenerNumeroSAP(
    linea?.PrecioConIVA,
    linea?.PriceAfterVAT,
    linea?.GrossPrice
  );

  if (precioSAP > 0) return precioSAP;

  const precioInfo = obtenerPrecioInfoSAP(linea);
  const factorIVA = obtenerFactorIVA(linea);

  return round4(precioInfo * factorIVA);
};

const obtenerLineTotalSAP = (linea) => {
  return obtenerNumeroSAP(
    linea?.LineTotal,
    linea?.RowTotal,
    linea?.TotalLinea,
    linea?.LineTotalSAP
  );
};

const obtenerVatSumLineaSAP = (linea) => {
  return obtenerNumeroSAP(
    linea?.VatSum,
    linea?.TaxTotal,
    linea?.TaxAmount,
    linea?.ImpuestoLinea,
    linea?.VatSumSAP
  );
};

const enriquecerLinea = (linea) => {
  const quantity = Number(linea.Quantity || 0);
  const precioInfo = round4(obtenerPrecioInfoSAP(linea));
  const precioConIVA = round4(obtenerPrecioConIVASAP(linea));

  return {
    ...linea,
    Quantity: quantity,
    PrecioInfo: precioInfo,
    PrecioConIVA: precioConIVA,
    LineTotalSAP: obtenerLineTotalSAP(linea),
    VatSumSAP: obtenerVatSumLineaSAP(linea),
    VatPercent: obtenerPorcentajeIVA(linea),
    TaxCode: linea.TaxCode || linea.VatGroup || linea.TaxCodeSAP || "",
    WarehouseCode: linea.WarehouseCode || linea.WhsCode || "",
  };
};

const recalcularLineasParaVista = (lines = []) => {
  return lines.map((linea) => enriquecerLinea(linea));
};

// Alias para conservar compatibilidad con el resto del componente.
// Ya no recalcula el IVA manualmente; solo normaliza los valores para mostrarlos.
const recalcularLineasConIva = recalcularLineasParaVista;

const calcularResumenDesdeSAP = (detalle, lines = []) => {
  const lineas = recalcularLineasParaVista(lines);

  const totalAntesDescuentoSAP = obtenerNumeroSAP(
    detalle?.TotalBeforeDiscount,
    detalle?.DocTotalBeforeDiscount,
    detalle?.TotalAntesDescuento,
    detalle?.TotalSinImpuesto,
    detalle?.SubTotal
  );

  const impuestoSAP = obtenerNumeroSAP(
    detalle?.VatSum,
    detalle?.TaxTotal,
    detalle?.Impuesto
  );

  const docTotalSAP = obtenerNumeroSAP(
    detalle?.DocTotal,
    detalle?.TotalDocumento
  );

  const totalAntesDescuentoCalculado = round2(
    lineas.reduce((acc, linea) => {
      const qty = Number(linea.Quantity || 0);
      const precioInfo = Number(linea.PrecioInfo || 0);

      const lineTotal = obtenerNumeroSAP(
        linea.LineTotalSAP,
        linea.LineTotal,
        linea.RowTotal,
        qty * precioInfo
      );

      return acc + lineTotal;
    }, 0)
  );

  const impuestoCalculado = round2(
    lineas.reduce((acc, linea) => {
      return acc + obtenerNumeroSAP(linea.VatSumSAP, linea.VatSum, linea.TaxTotal);
    }, 0)
  );

  const totalAntesDescuento =
    totalAntesDescuentoSAP > 0
      ? totalAntesDescuentoSAP
      : totalAntesDescuentoCalculado;

  const impuesto = impuestoSAP > 0 ? impuestoSAP : impuestoCalculado;

  const totalDocumento =
    docTotalSAP > 0
      ? docTotalSAP
      : round2(totalAntesDescuento + impuesto);

  return {
    lineas,
    totalAntesDescuento: round2(totalAntesDescuento),
    impuesto: round2(impuesto),
    totalDocumento: round2(totalDocumento),
  };
};

const calcularResumenDocumento = (lines = []) => {
  const lineas = recalcularLineasParaVista(lines);

  const totalAntesDescuento = round2(
    lineas.reduce((acc, linea) => {
      return acc + Number(linea.Quantity || 0) * Number(linea.PrecioInfo || 0);
    }, 0)
  );

  const totalDocumento = round2(
    lineas.reduce((acc, linea) => {
      const factorDocumento = obtenerFactorDocumentoLinea(linea);
      return (
        acc +
        Number(linea.Quantity || 0) *
          Number(linea.PrecioInfo || 0) *
          factorDocumento
      );
    }, 0)
  );

  const impuesto = round2(totalDocumento - totalAntesDescuento);

  return {
    lineas,
    totalAntesDescuento,
    impuesto,
    totalDocumento,
  };
};

const construirComentarioConUsuario = (nombreApellido, comentarioOriginal) => {
  const usuario = String(nombreApellido || "").trim();
  const comentario = String(comentarioOriginal || "").trim();

  if (!usuario) return comentario;
  if (!comentario) return usuario;

  if (comentario.startsWith(usuario)) return comentario;

  return `${usuario} - ${comentario}`;
};

const construirComentariosEntrada = ({ usuario, comments }) => {
  return construirComentarioConUsuario(usuario, comments);
};

const extraerComentarioBase = (textoCompleto, numAtCard, uFacNom) => {
  const prefijos = [];

  if (String(numAtCard || "").trim()) {
    prefijos.push(`${String(numAtCard).trim()}`);
  }

  if (String(uFacNom || "").trim()) {
    prefijos.push(`${String(uFacNom).trim()}`);
  }

  const prefijoCompleto = prefijos.join(", ");
  const texto = String(textoCompleto || "").trim();

  if (!prefijoCompleto) return texto;
  if (texto.startsWith(prefijoCompleto)) {
    return texto.slice(prefijoCompleto.length).replace(/^\s*,\s*/, "");
  }

  return texto;
};



const totalDentroDeRango = (totalActual, totalBase) => {
  return Math.abs(round2(totalActual) - round2(totalBase)) <= MAX_DIFERENCIA_TOTAL;
};

const ajustarPreciosParaTotalDocumento = (lines = [], targetTotalDocumento) => {
  const lineasActualizadas = recalcularLineasParaVista(lines).map((linea) => ({
    ...linea,
    PrecioInfo: Number(linea.PrecioInfo || 0),
    PrecioConIVA: Number(linea.PrecioConIVA || 0),
    Quantity: Number(linea.Quantity || 0),
    OpenQuantity: Number(linea.OpenQuantity || 0),
    WarehouseCode: linea.WarehouseCode || linea.WhsCode || "",
  }));

  const indicesEditables = lineasActualizadas
    .map((linea, index) => ({ linea, index }))
    .filter(
      ({ linea }) =>
        !linea.IsClosedLine &&
        Number(linea.OpenQuantity || 0) > 0 &&
        Number(linea.Quantity || 0) > 0
    );

  if (indicesEditables.length === 0) {
    const resumenSinCambio = calcularResumenDocumento(lineasActualizadas);
    return {
      lines: resumenSinCambio.lineas,
      totalAntesDescuento: resumenSinCambio.totalAntesDescuento,
      impuesto: resumenSinCambio.impuesto,
      totalDocumento: resumenSinCambio.totalDocumento,
    };
  }

  const esEditable = (index) =>
    indicesEditables.some((item) => item.index === index);

  const obtenerTotalDocumentoLinea = (linea) => {
    const qty = Number(linea.Quantity || 0);
    const precioInfo = Number(linea.PrecioInfo || 0);
    const factorDocumento = obtenerFactorDocumentoLinea(linea);
    return round2(qty * precioInfo * factorDocumento);
  };

  const totalNoEditableDocumento = round2(
    lineasActualizadas.reduce((acc, linea, index) => {
      if (esEditable(index)) return acc;
      return acc + obtenerTotalDocumentoLinea(linea);
    }, 0)
  );

  const totalEditableActualDocumento = round2(
    indicesEditables.reduce((acc, { linea }) => {
      return acc + obtenerTotalDocumentoLinea(linea);
    }, 0)
  );

  const objetivoEditableDocumento = round2(
    Number(targetTotalDocumento || 0) - totalNoEditableDocumento
  );

  if (objetivoEditableDocumento < 0) {
    const resumenSinCambio = calcularResumenDocumento(lineasActualizadas);
    return {
      lines: resumenSinCambio.lineas,
      totalAntesDescuento: resumenSinCambio.totalAntesDescuento,
      impuesto: resumenSinCambio.impuesto,
      totalDocumento: resumenSinCambio.totalDocumento,
    };
  }

  if (indicesEditables.length === 1) {
    const { index, linea } = indicesEditables[0];
    const qty = Number(linea.Quantity || 0);
    const factorDocumento = obtenerFactorDocumentoLinea(linea);

    if (qty > 0 && factorDocumento > 0) {
      const nuevoPrecioInfo = round4(objetivoEditableDocumento / (qty * factorDocumento));
      lineasActualizadas[index] = {
        ...linea,
        PrecioInfo: nuevoPrecioInfo,
        PrecioConIVA: round4(nuevoPrecioInfo * factorDocumento),
        PriceAfterVAT: round4(nuevoPrecioInfo * factorDocumento),
        GrossPrice: round4(nuevoPrecioInfo * factorDocumento),
      };
    }

    const resumen = calcularResumenDocumento(lineasActualizadas);

    return {
      lines: resumen.lineas,
      totalAntesDescuento: resumen.totalAntesDescuento,
      impuesto: resumen.impuesto,
      totalDocumento: resumen.totalDocumento,
    };
  }

  let acumuladoDocumento = 0;

  indicesEditables.forEach(({ linea, index }, posicion) => {
    const qty = Number(linea.Quantity || 0);
    const factorDocumento = obtenerFactorDocumentoLinea(linea);
    const subtotalDocumentoActual = obtenerTotalDocumentoLinea(linea);

    if (qty <= 0 || factorDocumento <= 0) return;

    if (posicion === indicesEditables.length - 1) {
      const restanteDocumento = round2(objetivoEditableDocumento - acumuladoDocumento);
      const nuevoPrecioInfo = round4(restanteDocumento / (qty * factorDocumento));

      lineasActualizadas[index] = {
        ...linea,
        PrecioInfo: nuevoPrecioInfo,
        PrecioConIVA: round4(nuevoPrecioInfo * factorDocumento),
        PriceAfterVAT: round4(nuevoPrecioInfo * factorDocumento),
        GrossPrice: round4(nuevoPrecioInfo * factorDocumento),
      };

      return;
    }

    let proporcion = 0;

    if (totalEditableActualDocumento > 0) {
      proporcion = subtotalDocumentoActual / totalEditableActualDocumento;
    } else {
      const totalQtyEditable = indicesEditables.reduce(
        (acc, item) => acc + Number(item.linea.Quantity || 0),
        0
      );

      proporcion = totalQtyEditable > 0 ? qty / totalQtyEditable : 0;
    }

    const subtotalObjetivoDocumentoLinea = round2(objetivoEditableDocumento * proporcion);
    const nuevoPrecioInfo = round4(subtotalObjetivoDocumentoLinea / (qty * factorDocumento));

    lineasActualizadas[index] = {
      ...linea,
      PrecioInfo: nuevoPrecioInfo,
      PrecioConIVA: round4(nuevoPrecioInfo * factorDocumento),
      PriceAfterVAT: round4(nuevoPrecioInfo * factorDocumento),
      GrossPrice: round4(nuevoPrecioInfo * factorDocumento),
    };

    acumuladoDocumento = round2(
      acumuladoDocumento +
        Number(lineasActualizadas[index].Quantity || 0) *
          Number(lineasActualizadas[index].PrecioInfo || 0) *
          factorDocumento
    );
  });

  const resumen = calcularResumenDocumento(lineasActualizadas);

  return {
    lines: resumen.lineas,
    totalAntesDescuento: resumen.totalAntesDescuento,
    impuesto: resumen.impuesto,
    totalDocumento: resumen.totalDocumento,
  };
};

const generarHTMLTermicoEntrada = ({
  logoUrl,
  docNum,
  formData,
  lineasImpresas,
  copyLabel = "INGRESO",
}) => {
  const filas = lineasImpresas
    .map(
      (linea) => `
        <div class="line-row">
          <div class="cell codigo">${escapeHtml(linea.ItemCode || "")}</div>
          <div class="cell descripcion">${escapeHtml(
            linea.ItemDescription || linea.Dscription || ""
          )}</div>
          <div class="cell almacen">${escapeHtml(
            linea.WarehouseCode || linea.WhsCode || ""
          )}</div>
          <div class="cell cantidad">${Number(linea.Quantity || 0).toLocaleString("es-GT", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}</div>
        </div>
      `
    )
    .join("");

  const fechaDocumento = formatDateDisplay(formData.DocDate || formData.TaxDate);
  const referencia = formData.NumAtCard || "";
  const proveedorCodigo = formData.CardCode || "";
  const proveedorNombre = formData.CardName || "";
  const comentarios = formData.Comments || "";
  const totalDocumentoImpresion = formatMoney2(
    formData.DocTotal ?? parseDecimal(formData.DocTotalInput) ?? 0
  );

  return `
    <!DOCTYPE html>
    <html lang="es">
      <head>
        <meta charset="UTF-8" />
        <title>Entrada de Compras</title>
        <style>
          @page {
            size: 80mm auto;
            margin: 0;
          }

          html,
          body {
            width: 80mm;
            margin: 0;
            padding: 0;
            font-family: Arial, Helvetica, sans-serif;
            color: #000;
            background: #fff;
          }

          * {
            box-sizing: border-box;
          }

          .print-wrapper {
            width: 80mm;
            margin: 0 auto;
            padding: 0;
          }

          .ticket {
            width: 80mm;
            padding: 0.5mm 4mm 1mm 4mm;
          }

          .logo-wrap {
            text-align: center;
            margin-bottom: 1mm;
          }

          .logo {
            max-width: 48mm;
            max-height: 18mm;
            object-fit: contain;
          }

          .title {
            text-align: center;
            font-size: 18px;
            font-weight: 700;
            margin-top: 0.3mm;
            line-height: 1.05;
          }

          .docno {
            text-align: center;
            font-size: 16px;
            margin-bottom: 2mm;
            line-height: 1.05;
          }

          .block {
            font-size: 12px;
            line-height: 1.3;
            margin-bottom: 1.5mm;
          }

          .centered {
            text-align: center;
          }

          .provider-name {
            margin: 0.5mm 0;
            word-break: break-word;
          }

          .copy-label {
            text-align: center;
            font-size: 20px;
            font-weight: 700;
            margin: 0.5mm 0 0.5mm 0;
            line-height: 1;
          }

          .lines-section {
            width: 100%;
            margin-top: 0;
          }

          .line-header,
          .line-row {
            display: grid;
            grid-template-columns: 30% 35% 20% 15%;
            column-gap: 0;
            width: 100%;
          }

          .line-header {
            border-bottom: 1px dashed #000;
            margin-bottom: 0.4mm;
          }

          .line-header div {
            font-size: 10px;
            font-weight: 700;
            line-height: 1.05;
            padding: 0.4mm 0.35mm;
            word-break: break-word;
          }

          .line-row {
            margin: 0;
            padding: 0;
            page-break-inside: avoid;
            break-inside: avoid;
          }

          .cell {
            font-size: 10px;
            line-height: 1.08;
            padding: 0.35mm 0.35mm;
            word-break: break-word;
            vertical-align: top;
          }

          .codigo {
            word-break: break-all;
          }

          .descripcion {
            word-break: break-word;
          }

          .almacen {
            word-break: break-word;
          }

          .cantidad {
            text-align: right;
            white-space: nowrap;
          }

          .comments {
            margin-top: 2mm;
            font-size: 11px;
            line-height: 1.25;
            font-weight: 700;
            word-break: break-word;
          }

          .document-total {
            margin-top: 1.5mm;
            font-size: 12px;
            line-height: 1.2;
            font-weight: 700;
            text-align: right;
            width: 100%;
          }

          .signature {
            margin-top: 12mm;
            font-size: 12px;
          }

          .footer {
            margin-top: 0.8mm;
            font-size: 9px;
            text-align: center;
            line-height: 1.1;
          }

          @media print {
            html,
            body {
              width: 80mm;
              margin: 0;
              padding: 0;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
        </style>
      </head>
      <body>
        <div class="print-wrapper">
          <div class="ticket">
            <div class="logo-wrap">
              ${logoUrl ? `<img src="${logoUrl}" alt="Logo" class="logo" />` : ""}
            </div>

            <div class="copy-label">${escapeHtml(copyLabel)}</div>
            
            <div class="docno">No ${escapeHtml(docNum || "")}</div>

            <div class="block centered">
              <div><strong>Fecha:</strong> ${escapeHtml(fechaDocumento)}</div>
              <div><strong>Proveedor:</strong> ${escapeHtml(proveedorCodigo)}</div>
              <div class="provider-name">${escapeHtml(proveedorNombre)}</div>
              <div><strong>Referencia:</strong> ${escapeHtml(referencia)}</div>
            </div>

            <div class="title">Entrada de Compras</div>

            <div class="lines-section">
              <div class="line-header">
                <div>Código</div>
                <div>Descripción</div>
                <div>Almacén ingresa</div>
                <div style="text-align:right;">Cant.</div>
              </div>
              ${filas}
            </div>

            <div class="comments">
              <strong>Comentarios:</strong> <strong>${escapeHtml(comentarios)}</strong>
            </div>

            <div class="document-total">
              Total del documento: Q${escapeHtml(totalDocumentoImpresion)}
            </div>

            <div class="signature">Vo Bo._______________________________</div>

            <div class="footer">
              Fecha y hora de impresión ${new Date().toLocaleString("es-GT")}
            </div>
          </div>
        </div>

        <script>
          window.onload = function () {
            setTimeout(() => {
              window.print();
            }, 250);
          };

          window.onafterprint = function () {
            setTimeout(() => window.close(), 300);
          };
        </script>
      </body>
    </html>
  `;
};

const imprimirUnaCopiaTermica = ({
  logoUrl,
  docNum,
  formData,
  copyLabel = "INGRESO",
}) => {
  return new Promise((resolve, reject) => {
    const lineasImpresas = (formData.DocumentLines || []).filter(
      (linea) => Number(linea.Quantity || 0) > 0
    );

    const html = generarHTMLTermicoEntrada({
      logoUrl,
      docNum,
      formData,
      lineasImpresas,
      copyLabel,
    });

    const printWindow = window.open("", "_blank", "width=420,height=900");

    if (!printWindow) {
      reject(new Error("El navegador bloqueó la ventana de impresión."));
      return;
    }

    let finalizado = false;

    const terminar = () => {
      if (finalizado) return;
      finalizado = true;
      resolve();
    };

    printWindow.onafterprint = () => {
      setTimeout(terminar, 300);
    };

    const pollClosed = setInterval(() => {
      if (printWindow.closed) {
        clearInterval(pollClosed);
        terminar();
      }
    }, 400);

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  });
};

const swalBase = {
  customClass: {
    container: "swal-container-front",
    popup: "swal-front",
  },
};

const imprimirEntradaTermicaSecuencial = async ({
  logoUrl,
  docNum,
  formData,
  copyLabel = "INGRESO",
}) => {
  await imprimirUnaCopiaTermica({
    logoUrl,
    docNum,
    formData,
    copyLabel,
  });

  await Swal.fire({
    ...swalBase,
    icon: "info",
    title: "Primera copia impresa",
    html: "Corte la primera copia y luego presione <b>Enter</b> o haga clic en <b>Continuar</b> para imprimir la segunda.",
    confirmButtonText: "Continuar",
    allowOutsideClick: false,
    allowEscapeKey: false,
  });

  await imprimirUnaCopiaTermica({
    logoUrl,
    docNum,
    formData,
    copyLabel,
  });
};

const EntradaMercancias = () => {
  const [ordenes, setOrdenes] = useState([]);
  const [almacenes, setAlmacenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [showOptions, setShowOptions] = useState(false);

  const [entradasCancelacion, setEntradasCancelacion] = useState([]);
  const [searchCancelacion, setSearchCancelacion] = useState("");
  const [showOptionsCancelacion, setShowOptionsCancelacion] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [detalleOrden, setDetalleOrden] = useState(null);
  const [formData, setFormData] = useState(null);
  const [errorDetalle, setErrorDetalle] = useState("");

  const [modalCancelacionOpen, setModalCancelacionOpen] = useState(false);
  const [detalleEntradaCancelacion, setDetalleEntradaCancelacion] = useState(null);
  const [formDataCancelacion, setFormDataCancelacion] = useState(null);
  const [errorDetalleCancelacion, setErrorDetalleCancelacion] = useState("");

  const [entradasDevolucion, setEntradasDevolucion] = useState([]);
  const [searchDevolucion, setSearchDevolucion] = useState("");
  const [showOptionsDevolucion, setShowOptionsDevolucion] = useState(false);
  const [modalDevolucionOpen, setModalDevolucionOpen] = useState(false);
  const [detalleEntradaDevolucion, setDetalleEntradaDevolucion] = useState(null);
  const [formDataDevolucion, setFormDataDevolucion] = useState(null);
  const [errorDetalleDevolucion, setErrorDetalleDevolucion] = useState("");

  const [, setCurrentUserId] = useState("");
  const [nombreApellidoLogueado, setNombreApellidoLogueado] = useState("");

  const ordenSearchRef = useRef(null);
  const cancelacionSearchRef = useRef(null);
  const devolucionSearchRef = useRef(null);
  const caretRestoreRef = useRef(null);

  const getSanitizedCaretPosition = (value, position) => {
    const textBeforeCaret = String(value || "").slice(0, position || 0);
    return sanitizeDecimalInput(textBeforeCaret.replaceAll(",", "")).length;
  };

  const guardarPosicionCursorDecimal = (event, inputKey) => {
    const target = event?.target;
    if (!target || inputKey == null) return;

    caretRestoreRef.current = {
      inputKey,
      start: getSanitizedCaretPosition(target.value, target.selectionStart),
      end: getSanitizedCaretPosition(target.value, target.selectionEnd),
    };
  };

  useLayoutEffect(() => {
    const caret = caretRestoreRef.current;
    if (!caret) return;

    const selector = `[data-caret-key="${caret.inputKey}"]`;
    const input = document.querySelector(selector);

    if (input && document.activeElement === input && typeof input.setSelectionRange === "function") {
      const valueLength = String(input.value || "").length;
      const start = Math.min(caret.start, valueLength);
      const end = Math.min(caret.end, valueLength);
      input.setSelectionRange(start, end);
    }

    caretRestoreRef.current = null;
  }, [formData]);

  useEffect(() => {
    let userid = "";
    let nombresap = "";
    let apellidosap = "";
    let NombreApellido = "";
    const token = localStorage.getItem("token");
    let token_Data = null;

    try {
      token_Data = JSON.parse(token);
    } catch (error) {
      token_Data = null;
    }

    if (token_Data !== null && Array.isArray(token_Data)) {
      token_Data.forEach((element) => {
        userid = element.U_UserId;
        nombresap = element.Nombre;
        apellidosap = element.Apellido;
        NombreApellido = `${nombresap || ""} ${apellidosap || ""}`.trim();
      });
    }

    if (token_Data === null) {
      window.location.href = "";
      return;
    }

    setCurrentUserId(String(userid || ""));
    setNombreApellidoLogueado(NombreApellido);
  }, []);

  const showLoadingAlert = (title) => {
    Swal.fire({
      ...swalBase,
      title,
      text: "Por favor espere...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
  };

  const closeLoadingAlert = () => {
    Swal.close();
  };

  const fetchAlmacenes = async () => {
    try {
      const res = await fetch(API_URL_ALMACENES, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "API-Key": API_KEY,
        },
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Error ${res.status}: ${text}`);
      }

      const data = await res.json();

      setAlmacenes(Array.isArray(data) ? data : data.data || []);
    } catch (err) {
      console.error("Error cargando almacenes:", err);

      Swal.fire({
        ...swalBase,
        icon: "error",
        title: "Error",
        text: "No fue posible cargar la lista de almacenes.",
      });
    }
  };

  const fetchOrdenes = async () => {
    try {
      setLoading(true);
      setError("");
      showLoadingAlert("Cargando órdenes de compra");

      const res = await fetch(API_URL_ORDENES, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "API-Key": API_KEY,
        },
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Error ${res.status}: ${text}`);
      }

      const data = await res.json();

      if (data.answer !== "true") {
        throw new Error(data.message || "Error al obtener las órdenes de compra");
      }

      setOrdenes(data.data || []);
      closeLoadingAlert();
    } catch (err) {
      console.error(err);
      setError(err.message || "Ocurrió un error al cargar las órdenes");
      closeLoadingAlert();

      Swal.fire({
        ...swalBase,
        icon: "error",
        title: "Error",
        text: err.message || "Ocurrió un error al cargar las órdenes",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchEntradasMercanciaCancelacion = async () => {
    try {
      const res = await fetch(API_URL_ENTRADAS_MERCANCIA_CANCELACION, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "API-Key": API_KEY,
        },
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Error ${res.status}: ${text}`);
      }

      const data = await res.json();

      if (data.answer !== "true") {
        throw new Error(
          data.message || "Error al obtener las entradas de mercancía"
        );
      }

      setEntradasCancelacion(data.data || []);
    } catch (err) {
      console.error("Error cargando entradas de mercancía:", err);

      Swal.fire({
        ...swalBase,
        icon: "error",
        title: "Error",
        text:
          err.message ||
          "No fue posible cargar las entradas de mercancía para cancelación.",
      });
    }
  };

  const construirFormDataEntradaCancelacion = (detalle, nombreUsuario = nombreApellidoLogueado) => {
    const documentLines = Array.isArray(detalle?.DocumentLines)
      ? detalle.DocumentLines.map((linea) => {
          const quantity = Number(linea.Quantity || linea.OpenQuantity || 0);
          const precioInfo =
            linea?.Price != null
              ? Number(linea.Price)
              : linea?.UnitPrice != null
              ? Number(linea.UnitPrice)
              : linea?.PriceBefDi != null
              ? Number(linea.PriceBefDi)
              : 0;

          const factorIVA = obtenerFactorIVA(linea);

          return enriquecerLinea({
            ...linea,
            Quantity: quantity,
            OpenQuantity: quantity,
            PrecioInfo: precioInfo,
            PrecioConIVA:
              linea?.PriceAfterVAT != null
                ? Number(linea.PriceAfterVAT)
                : round4(precioInfo * factorIVA),
            IsClosedLine: false,
            WarehouseCode: linea.WarehouseCode || linea.WhsCode || "",
          });
        })
      : [];

    const resumen = calcularResumenDesdeSAP(detalle, documentLines);
    const comentarioBase = detalle?.Comments || "";

    return {
      DocEntry: detalle?.DocEntry || "",
      DocNum: detalle?.DocNum || "",
      CardCode: detalle?.CardCode || "",
      CardName: detalle?.CardName || "",
      NumAtCard: detalle?.NumAtCard || "",
      TaxDate: formatDateForInput(detalle?.TaxDate),
      DocDueDate: formatDateForInput(detalle?.DocDueDate),
      DocDate: formatDateForInput(detalle?.DocDate),
      U_FacNit: detalle?.U_FacNit || "",
      U_FacNom: detalle?.U_FacNom || "",
      CommentsBase: construirComentarioConUsuario(nombreUsuario, comentarioBase),
      TotalAntesDescuento: resumen.totalAntesDescuento,
      Impuesto: resumen.impuesto,
      DocTotal: resumen.totalDocumento,
      DocTotalInput: formatMoney2(resumen.totalDocumento),
      InitialDocTotal: resumen.totalDocumento,
      ValidationBaseDocTotal: resumen.totalDocumento,
      DocumentLines: resumen.lineas,
    };
  };

  const fetchDetalleOrden = async (docEntry) => {
    try {
      setErrorDetalle("");
      setDetalleOrden(null);
      setFormData(null);

      showLoadingAlert("Cargando detalle de la orden");

      const res = await fetch(`${API_URL_DETALLE}/${docEntry}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "API-Key": API_KEY,
        },
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Error ${res.status}: ${text}`);
      }

      const data = await res.json();

      if (data.answer !== "true") {
        throw new Error(data.message || "Error al obtener el detalle de la orden");
      }

      const detalle = data.data || null;
      setDetalleOrden(detalle);

      console.log("DETALLE ORDEN SAP:", detalle);
      console.table(detalle?.DocumentLines || []);

      const documentLines = Array.isArray(detalle?.DocumentLines)
        ? detalle.DocumentLines.map((linea) => {
            const cantidadPendiente = obtenerCantidadPendiente(linea);
            const precioInfo = obtenerPrecioInfoSAP(linea);
            const precioConIVA = obtenerPrecioConIVASAP(linea);

            return enriquecerLinea({
              ...linea,
              OpenQuantity: cantidadPendiente,
              Quantity: cantidadPendiente,
              PrecioInfo: precioInfo,
              PrecioConIVA: precioConIVA,
              LineTotalSAP: obtenerLineTotalSAP(linea),
              VatSumSAP: obtenerVatSumLineaSAP(linea),
              IsClosedLine: cantidadPendiente <= 0,
              WarehouseCode: linea.WarehouseCode || linea.WhsCode || "",
            });
          })
        : [];

      const resumen = calcularResumenDesdeSAP(detalle, documentLines);
      const comentarioBase = detalle?.Comments || "";
      const fechaHoyDocumento = getTodayForInput();

      setFormData({
        DocEntry: detalle?.DocEntry || "",
        DocNum: detalle?.DocNum || "",
        CardCode: detalle?.CardCode || "",
        CardName: detalle?.CardName || "",
        NumAtCard: detalle?.NumAtCard || "",
        TaxDate: fechaHoyDocumento,
        DocDueDate: fechaHoyDocumento,
        DocDate: fechaHoyDocumento,
        U_FacNit: detalle?.U_FacNit || "",
        U_FacNom: detalle?.U_FacNom || "",
        CommentsBase: construirComentarioConUsuario(nombreApellidoLogueado, comentarioBase),
        TotalAntesDescuento: resumen.totalAntesDescuento,
        Impuesto: resumen.impuesto,
        DocTotal: resumen.totalDocumento,
        DocTotalInput: formatMoney2(resumen.totalDocumento),
        InitialDocTotal: resumen.totalDocumento,
        ValidationBaseDocTotal: resumen.totalDocumento,
        DocumentLines: resumen.lineas,
      });

      closeLoadingAlert();
      setModalOpen(true);
    } catch (err) {
      console.error(err);
      setErrorDetalle(err.message || "Ocurrió un error al obtener el detalle");
      closeLoadingAlert();

      Swal.fire({
        ...swalBase,
        icon: "error",
        title: "Error al cargar detalle",
        text: err.message || "Ocurrió un error al obtener el detalle",
      });
    }
  };

  const fetchEntradasMercanciaDevolucion = async () => {
    try {
      const res = await fetch(API_URL_ENTRADAS_MERCANCIA_DEVOLUCION, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "API-Key": API_KEY,
        },
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Error ${res.status}: ${text}`);
      }

      const data = await res.json();

      if (data.answer !== "true") {
        throw new Error(
          data.message || "Error al obtener las entradas de mercancía para devolución"
        );
      }

      setEntradasDevolucion(data.data || []);
    } catch (err) {
      console.error("Error cargando entradas de mercancía para devolución:", err);

      Swal.fire({
        ...swalBase,
        icon: "error",
        title: "Error",
        text:
          err.message ||
          "No fue posible cargar las entradas de mercancía para devolución.",
      });
    }
  };

  const fetchDetalleEntradaCancelacion = async (docEntry) => {
    try {
      setErrorDetalleCancelacion("");
      setDetalleEntradaCancelacion(null);
      setFormDataCancelacion(null);

      showLoadingAlert("Cargando detalle de la entrada");

      const res = await fetch(`${API_URL_DETALLE_ENTRADA_MERCANCIA}/${docEntry}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "API-Key": API_KEY,
        },
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Error ${res.status}: ${text}`);
      }

      const data = await res.json();

      if (data.answer !== "true") {
        throw new Error(data.message || "Error al obtener el detalle de la entrada");
      }

      const detalle = data.data || null;
      setDetalleEntradaCancelacion(detalle);
      setFormDataCancelacion(construirFormDataEntradaCancelacion(detalle, nombreApellidoLogueado));

      closeLoadingAlert();
      setModalCancelacionOpen(true);
    } catch (err) {
      console.error(err);
      setErrorDetalleCancelacion(
        err.message || "Ocurrió un error al obtener el detalle de la entrada"
      );
      closeLoadingAlert();

      Swal.fire({
        ...swalBase,
        icon: "error",
        title: "Error al cargar detalle",
        text: err.message || "Ocurrió un error al obtener el detalle de la entrada",
      });
    }
  };

  const fetchDetalleEntradaDevolucion = async (docEntry) => {
    try {
      setErrorDetalleDevolucion("");
      setDetalleEntradaDevolucion(null);
      setFormDataDevolucion(null);

      showLoadingAlert("Cargando detalle de la entrada para devolución");

      const res = await fetch(`${API_URL_DETALLE_ENTRADA_MERCANCIA}/${docEntry}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "API-Key": API_KEY,
        },
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Error ${res.status}: ${text}`);
      }

      const data = await res.json();

      if (data.answer !== "true") {
        throw new Error(data.message || "Error al obtener el detalle de la entrada");
      }

      const detalle = data.data || null;
      setDetalleEntradaDevolucion(detalle);
      setFormDataDevolucion(construirFormDataEntradaCancelacion(detalle, nombreApellidoLogueado));

      closeLoadingAlert();
      setModalDevolucionOpen(true);
    } catch (err) {
      console.error(err);
      setErrorDetalleDevolucion(
        err.message || "Ocurrió un error al obtener el detalle de la entrada"
      );
      closeLoadingAlert();

      Swal.fire({
        ...swalBase,
        icon: "error",
        title: "Error al cargar detalle",
        text: err.message || "Ocurrió un error al obtener el detalle de la entrada",
      });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;

      if (ordenSearchRef.current && !ordenSearchRef.current.contains(target)) {
        setShowOptions(false);
      }

      if (
        cancelacionSearchRef.current &&
        !cancelacionSearchRef.current.contains(target)
      ) {
        setShowOptionsCancelacion(false);
      }

      if (
        devolucionSearchRef.current &&
        !devolucionSearchRef.current.contains(target)
      ) {
        setShowOptionsDevolucion(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    fetchOrdenes();
    fetchAlmacenes();
    fetchEntradasMercanciaCancelacion();
    fetchEntradasMercanciaDevolucion();
  }, []);

  const filteredOrdenes = useMemo(() => {
    if (!search.trim()) return ordenes;

    const q = search.toLowerCase();

    return ordenes.filter((item) => {
      const cardName = String(item.CardName || "").toLowerCase();
      const cardCode = String(item.CardCode || "").toLowerCase();
      const docNum = String(item.DocNum || "").toLowerCase();
      const texto = String(item.DocNum_CardName || "").toLowerCase();

      return (
        cardName.includes(q) ||
        cardCode.includes(q) ||
        docNum.includes(q) ||
        texto.includes(q)
      );
    });
  }, [ordenes, search]);

  const filteredEntradasCancelacion = useMemo(() => {
    if (!searchCancelacion.trim()) return entradasCancelacion;

    const q = searchCancelacion.toLowerCase();

    return entradasCancelacion.filter((item) => {
      const cardName = String(item.CardName || "").toLowerCase();
      const cardCode = String(item.CardCode || "").toLowerCase();
      const docNum = String(item.DocNum || "").toLowerCase();
      const texto = String(item.DocNum_CardName || "").toLowerCase();

      return (
        cardName.includes(q) ||
        cardCode.includes(q) ||
        docNum.includes(q) ||
        texto.includes(q)
      );
    });
  }, [entradasCancelacion, searchCancelacion]);

  const filteredEntradasDevolucion = useMemo(() => {
    if (!searchDevolucion.trim()) return entradasDevolucion;

    const q = searchDevolucion.toLowerCase();

    return entradasDevolucion.filter((item) => {
      const cardName = String(item.CardName || "").toLowerCase();
      const cardCode = String(item.CardCode || "").toLowerCase();
      const docNum = String(item.DocNum || "").toLowerCase();
      const texto = String(item.DocNum_CardName || "").toLowerCase();

      return (
        cardName.includes(q) ||
        cardCode.includes(q) ||
        docNum.includes(q) ||
        texto.includes(q)
      );
    });
  }, [entradasDevolucion, searchDevolucion]);

  const comentarioCompleto = formData?.CommentsBase ?? "";
  const comentarioCompletoCancelacion = formDataCancelacion?.CommentsBase ?? "";
  const comentarioCompletoDevolucion = formDataDevolucion?.CommentsBase ?? "";

  const handleSelectOrden = (orden) => {
    setSearch(orden.DocNum_CardName || `${orden.DocNum} - ${orden.CardName}`);
    setShowOptions(false);
    fetchDetalleOrden(orden.DocEntry);
  };

  const handleSelectEntradaCancelacion = (entrada) => {
    setSearchCancelacion(
      entrada.DocNum_CardName || `${entrada.DocNum} - ${entrada.CardName}`
    );

    setShowOptionsCancelacion(false);
    fetchDetalleEntradaCancelacion(entrada.DocEntry);
  };

  const handleSelectEntradaDevolucion = (entrada) => {
    setSearchDevolucion(
      entrada.DocNum_CardName || `${entrada.DocNum} - ${entrada.CardName}`
    );

    setShowOptionsDevolucion(false);
    fetchDetalleEntradaDevolucion(entrada.DocEntry);
  };

  const handleHeaderChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCommentsChange = (value) => {
    setFormData((prev) =>
      prev
        ? {
            ...prev,
            CommentsBase: value,
          }
        : prev
    );
  };

  const actualizarResumenEnEstado = (updatedLines, detalleBase = formData) => {
    const lineas = recalcularLineasParaVista(updatedLines);

    const totalAntesDescuento = round2(
      lineas.reduce((acc, linea) => {
        return acc + Number(linea.Quantity || 0) * Number(linea.PrecioInfo || 0);
      }, 0)
    );

    const impuestoBase = Number(detalleBase?.Impuesto || 0);
    const subtotalBase = Number(detalleBase?.TotalAntesDescuento || 0);
    const porcentajeImpuestoDocumento = subtotalBase > 0 ? impuestoBase / subtotalBase : 0;
    const impuesto = round2(totalAntesDescuento * porcentajeImpuestoDocumento);
    const totalDocumento = round2(totalAntesDescuento + impuesto);

    return {
      DocumentLines: lineas,
      TotalAntesDescuento: totalAntesDescuento,
      Impuesto: impuesto,
      DocTotal: totalDocumento,
      DocTotalInput: formatMoney2(totalDocumento),
      ValidationBaseDocTotal: totalDocumento,
    };
  };

  const handleLineChange = (index, field, value, event = null) => {
    if (field === "Quantity" || field === "PrecioInfo") {
      guardarPosicionCursorDecimal(event, `line-${index}-${field}`);
    }

    setFormData((prev) => {
      if (!prev) return prev;

      const updatedLines = [...prev.DocumentLines];
      const lineaActual = updatedLines[index];

      if (lineaActual.IsClosedLine || Number(lineaActual.OpenQuantity || 0) <= 0) {
        return prev;
      }

      let nuevoValor = value;
      const extraFields = {};

      if (field === "Quantity" || field === "PrecioInfo") {
        const limpio = sanitizeDecimalInput(value);
        nuevoValor = parseDecimal(limpio);
        extraFields[`${field}Input`] = limpio;
      }

      updatedLines[index] = enriquecerLinea({
        ...updatedLines[index],
        ...extraFields,
        [field]: nuevoValor,
      });

      const resumenActualizado = actualizarResumenEnEstado(updatedLines);

      return {
        ...prev,
        ...resumenActualizado,
      };
    });
  };

  const handleLineInputBlur = (index, field) => {
    setFormData((prev) => {
      if (!prev) return prev;

      const updatedLines = [...prev.DocumentLines];
      const lineaActual = updatedLines[index];

      if (!lineaActual) return prev;

      const inputField = `${field}Input`;
      const valorNumerico = parseDecimal(lineaActual[inputField] ?? lineaActual[field]);

      updatedLines[index] = enriquecerLinea({
        ...lineaActual,
        [inputField]: valorNumerico ? String(valorNumerico) : "0",
        [field]: valorNumerico,
      });

      const resumenActualizado = actualizarResumenEnEstado(updatedLines);

      return {
        ...prev,
        ...resumenActualizado,
      };
    });
  };

  const handleEliminarLinea = async (index) => {
    const confirmacion = await Swal.fire({
      ...swalBase,
      icon: "warning",
      title: "Eliminar línea",
      text: "¿Desea eliminar esta línea del detalle?",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (!confirmacion.isConfirmed) return;

    setFormData((prev) => {
      if (!prev) return prev;

      const updatedLines = prev.DocumentLines.filter((_, i) => i !== index);
      const resumenActualizado = actualizarResumenEnEstado(updatedLines);

      return {
        ...prev,
        ...resumenActualizado,
      };
    });
  };

  const handleDocTotalInputChange = (value, event = null) => {
    guardarPosicionCursorDecimal(event, "doc-total");

    const limpio = sanitizeDecimalInput(String(value || "").replaceAll(",", ""));

    setFormData((prev) =>
      prev
        ? {
            ...prev,
            DocTotalInput: limpio,
          }
        : prev
    );
  };

  const obtenerAjusteDesdeTotalDocumento = ({ mostrarAlerta = true } = {}) => {
    if (!formData) return null;

    const rawValue = String(formData.DocTotalInput ?? "").replaceAll(",", "").trim();

    if (!rawValue) {
      if (mostrarAlerta) {
        Swal.fire({
          ...swalBase,
          icon: "warning",
          title: "Total inválido",
          text: "Debe ingresar un total del documento válido.",
        });
      }
      return null;
    }

    const nuevoTotalObjetivo = parseDecimal(rawValue);

    if (Number.isNaN(nuevoTotalObjetivo) || nuevoTotalObjetivo <= 0) {
      if (mostrarAlerta) {
        Swal.fire({
          ...swalBase,
          icon: "warning",
          title: "Total inválido",
          text: "Debe ingresar un número válido en Total del documento.",
        });
      }
      return null;
    }

    const resumenLineasActual = calcularResumenDocumento(formData.DocumentLines || []);
    const totalBaseValidacion = Number(
      resumenLineasActual.totalDocumento || formData.ValidationBaseDocTotal || 0
    );

    if (!totalDentroDeRango(nuevoTotalObjetivo, totalBaseValidacion)) {
      if (mostrarAlerta) {
        Swal.fire({
          ...swalBase,
          icon: "warning",
          title: "Total fuera de rango",
          text: `El total del documento no puede ser menor ni mayor a Q${MAX_DIFERENCIA_TOTAL.toFixed(
            2
          )} del total esperado según las líneas actuales (Q${formatMoney2(totalBaseValidacion)}).`,
        });
      }
      return null;
    }

    return ajustarPreciosParaTotalDocumento(
      formData.DocumentLines,
      nuevoTotalObjetivo
    );
  };

  const aplicarAjusteDesdeTotalDocumento = ({ mostrarAlerta = true } = {}) => {
    const ajuste = obtenerAjusteDesdeTotalDocumento({ mostrarAlerta });
    if (!ajuste) return false;

    setFormData((prev) =>
      prev
        ? {
            ...prev,
            DocumentLines: ajuste.lines,
            TotalAntesDescuento: ajuste.totalAntesDescuento,
            Impuesto: ajuste.impuesto,
            DocTotal: ajuste.totalDocumento,
            DocTotalInput: formatMoney2(ajuste.totalDocumento),
          }
        : prev
    );

    return true;
  };

  const handleGuardar = async () => {
    try {
      if (!formData || !Array.isArray(formData.DocumentLines)) {
        Swal.fire({
          ...swalBase,
          icon: "error",
          title: "Error",
          text: "No hay información para procesar la entrada de mercancía.",
        });
        return;
      }

      if (!String(formData.NumAtCard || "").trim()) {
        Swal.fire({
          ...swalBase,
          icon: "warning",
          title: "Campo obligatorio",
          text: "Debe ingresar el campo NumAtCard para realizar la entrada de mercancía.",
        });
        return;
      }

      const ajusteTotal = obtenerAjusteDesdeTotalDocumento({ mostrarAlerta: true });
      if (!ajusteTotal) return;

      const formDataAjustado = {
        ...formData,
        DocumentLines: ajusteTotal.lines,
        TotalAntesDescuento: ajusteTotal.totalAntesDescuento,
        Impuesto: ajusteTotal.impuesto,
        DocTotal: ajusteTotal.totalDocumento,
        DocTotalInput: formatMoney2(ajusteTotal.totalDocumento),
      };

      setFormData((prev) =>
        prev
          ? {
              ...prev,
              DocumentLines: ajusteTotal.lines,
              TotalAntesDescuento: ajusteTotal.totalAntesDescuento,
              Impuesto: ajusteTotal.impuesto,
              DocTotal: ajusteTotal.totalDocumento,
              DocTotalInput: formatMoney2(ajusteTotal.totalDocumento),
            }
          : prev
      );

      const lineasFinales = recalcularLineasParaVista(formDataAjustado.DocumentLines);

      const lineasConCantidad = lineasFinales.filter(
        (linea) => !lineaEstaCerrada(linea) && Number(linea.Quantity || 0) > 0
      );

      if (lineasConCantidad.length === 0) {
        Swal.fire({
          ...swalBase,
          icon: "warning",
          title: "Sin cantidades",
          text: "Debe ingresar al menos una línea abierta con cantidad mayor a 0.",
        });
        return;
      }

      const lineaInvalida = lineasConCantidad.find(
        (linea) => Number(linea.Quantity || 0) > Number(linea.OpenQuantity || 0)
      );

      if (lineaInvalida) {
        Swal.fire({
          ...swalBase,
          icon: "error",
          title: "Cantidad inválida",
          text: `La cantidad del artículo ${lineaInvalida.ItemCode || ""} no puede ser mayor a la cantidad pendiente.`,
        });
        return;
      }

      const lineaSinAlmacen = lineasConCantidad.find(
        (linea) => !String(linea.WarehouseCode || linea.WhsCode || "").trim()
      );

      if (lineaSinAlmacen) {
        Swal.fire({
          ...swalBase,
          icon: "warning",
          title: "Almacén requerido",
          text: `Debe seleccionar un almacén para el artículo ${lineaSinAlmacen.ItemCode || ""}.`,
        });
        return;
      }

      const comentariosFinales = construirComentariosEntrada({
        usuario: nombreApellidoLogueado,
        comments: formData.CommentsBase,
      });

      const payload = {
        CardCode: formDataAjustado.CardCode,
        DocDate: formDataAjustado.DocDate || null,
        DocDueDate: formDataAjustado.DocDueDate || null,
        TaxDate: formDataAjustado.TaxDate || null,
        NumAtCard: formDataAjustado.NumAtCard || "",
        Comments: comentariosFinales,
        U_FacNit: formDataAjustado.U_FacNit || "",
        U_FacNom: formDataAjustado.U_FacNom || "",
        DocTotalManual: round2(formDataAjustado.DocTotal || 0),
        DocumentLines: lineasConCantidad.map((linea) => ({
          BaseType: 22,
          BaseEntry: Number(formDataAjustado.DocEntry),
          BaseLine: Number(linea.LineNum),
          Quantity: Number(linea.Quantity || 0),
          UnitPrice: round4(Number(linea.PrecioInfo || 0)),
          Price: round4(Number(linea.PrecioInfo || 0)),
          PriceAfterVAT: round4(
            Number(linea.PrecioConIVA || 0) ||
              Number(linea.PrecioInfo || 0) * obtenerFactorDocumentoLinea(linea)
          ),
          GrossPrice: round4(
            Number(linea.PrecioConIVA || 0) ||
              Number(linea.PrecioInfo || 0) * obtenerFactorDocumentoLinea(linea)
          ),
          LineTotal: round2(
            Number(linea.Quantity || 0) * Number(linea.PrecioInfo || 0)
          ),
          WarehouseCode: linea.WarehouseCode || linea.WhsCode || "",
          ItemCode: linea.ItemCode || "",
          ItemDescription: linea.ItemDescription || linea.Dscription || "",
        })),
      };

      const confirmacion = await Swal.fire({
        ...swalBase,
        icon: "question",
        title: "Confirmar entrada",
        text: "¿Desea crear la entrada de mercancía con las cantidades indicadas?",
        showCancelButton: true,
        confirmButtonText: "Sí, crear",
        cancelButtonText: "Cancelar",
      });

      if (!confirmacion.isConfirmed) return;

      Swal.fire({
        ...swalBase,
        title: "Creando entrada de mercancía",
        text: "Por favor espere...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const res = await fetch(API_URL_CREAR_ENTRADA, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "API-Key": API_KEY,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      Swal.close();

      if (!res.ok || data.answer !== "true") {
        throw new Error(data.message || "No fue posible crear la entrada de mercancía.");
      }

      await Swal.fire({
        ...swalBase,
        icon: "success",
        title: "Proceso exitoso",
        text: data.message || "Entrada de mercancía creada correctamente.",
        confirmButtonText: "Imprimir",
      });

      const docNumCreado = data.doc_num || "";

      await imprimirEntradaTermicaSecuencial({
        logoUrl: LOGO_TICKET,
        docNum: docNumCreado,
        formData: {
          ...formDataAjustado,
          DocumentLines: lineasConCantidad,
          Comments: comentariosFinales,
        },
      });

      closeModal();
      fetchOrdenes();
    } catch (err) {
      console.error(err);
      Swal.close();

      Swal.fire({
        ...swalBase,
        icon: "error",
        title: "Error al crear entrada",
        text: err.message || "Ocurrió un error al crear la entrada de mercancía.",
      });
    }
  };

  const handleCommentsCancelacionChange = (value) => {
    setFormDataCancelacion((prev) =>
      prev
        ? {
            ...prev,
            CommentsBase: value,
          }
        : prev
    );
  };

  const closeModalCancelacion = () => {
    setModalCancelacionOpen(false);
    setDetalleEntradaCancelacion(null);
    setFormDataCancelacion(null);
    setErrorDetalleCancelacion("");
  };

  const handleCancelarEntradaMercancia = async () => {
    try {
      if (!formDataCancelacion?.DocEntry) {
        Swal.fire({
          ...swalBase,
          icon: "error",
          title: "Error",
          text: "No hay una entrada de mercancía seleccionada para cancelar.",
        });
        return;
      }

      const confirmacion = await Swal.fire({
        ...swalBase,
        icon: "warning",
        title: "Confirmar cancelación",
        text: `¿Desea cancelar la entrada de mercancía ${formDataCancelacion.DocNum || ""}?`,
        showCancelButton: true,
        confirmButtonText: "Sí, cancelar",
        cancelButtonText: "No cancelar",
      });

      if (!confirmacion.isConfirmed) return;

      Swal.fire({
        ...swalBase,
        title: "Cancelando entrada de mercancía",
        text: "Por favor espere...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const res = await fetch(
        `${API_URL_DETALLE_ENTRADA_MERCANCIA}/${formDataCancelacion.DocEntry}/cancel`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "API-Key": API_KEY,
          },
        }
      );

      const data = await res.json().catch(() => ({}));

      Swal.close();

      if (!res.ok || data.answer !== "true") {
        throw new Error(
          data.message || "No fue posible cancelar la entrada de mercancía."
        );
      }

      const comentariosFinales = construirComentariosEntrada({
        usuario: nombreApellidoLogueado,
        comments: formDataCancelacion.CommentsBase,
      });

      await Swal.fire({
        ...swalBase,
        icon: "success",
        title: "Cancelación exitosa",
        text: data.message || "Entrada de mercancía cancelada correctamente.",
        confirmButtonText: "Imprimir",
      });

      await imprimirEntradaTermicaSecuencial({
        logoUrl: LOGO_TICKET,
        docNum: formDataCancelacion.DocNum || "",
        formData: {
          ...formDataCancelacion,
          Comments: comentariosFinales,
        },
        copyLabel: "CANCELADO",
      });

      closeModalCancelacion();
      fetchEntradasMercanciaCancelacion();
      fetchOrdenes();
    } catch (err) {
      console.error(err);
      Swal.close();

      Swal.fire({
        ...swalBase,
        icon: "error",
        title: "Error al cancelar entrada",
        text: err.message || "Ocurrió un error al cancelar la entrada de mercancía.",
      });
    }
  };

  const handleCommentsDevolucionChange = (value) => {
    setFormDataDevolucion((prev) =>
      prev
        ? {
            ...prev,
            CommentsBase: value,
          }
        : prev
    );
  };

  const closeModalDevolucion = () => {
    setModalDevolucionOpen(false);
    setDetalleEntradaDevolucion(null);
    setFormDataDevolucion(null);
    setErrorDetalleDevolucion("");
  };

  const handleCrearDevolucionMercancia = async () => {
    try {
      if (!formDataDevolucion?.DocEntry) {
        Swal.fire({
          ...swalBase,
          icon: "error",
          title: "Error",
          text: "No hay una entrada de mercancía seleccionada para devolución.",
        });
        return;
      }

      const lineasFinales = recalcularLineasConIva(
        formDataDevolucion.DocumentLines || []
      );

      const lineasConCantidad = lineasFinales.filter(
        (linea) => Number(linea.Quantity || 0) > 0
      );

      if (lineasConCantidad.length === 0) {
        Swal.fire({
          ...swalBase,
          icon: "warning",
          title: "Sin líneas",
          text: "La entrada seleccionada no tiene líneas válidas para devolución.",
        });
        return;
      }

      const comentariosFinales = construirComentariosEntrada({
        usuario: nombreApellidoLogueado,
        comments: formDataDevolucion.CommentsBase,
      });

      const payload = {
        CardCode: formDataDevolucion.CardCode,
        DocDate: formDataDevolucion.DocDate || null,
        DocDueDate: formDataDevolucion.DocDueDate || null,
        TaxDate: formDataDevolucion.TaxDate || null,
        NumAtCard: formDataDevolucion.NumAtCard || "",
        Comments: comentariosFinales,
        U_FacNit: formDataDevolucion.U_FacNit || "",
        U_FacNom: formDataDevolucion.U_FacNom || "",
        DocumentLines: lineasConCantidad.map((linea) => ({
          BaseType: 20,
          BaseEntry: Number(formDataDevolucion.DocEntry),
          BaseLine: Number(linea.LineNum),
          Quantity: Number(linea.Quantity || 0),
          WarehouseCode: linea.WarehouseCode || linea.WhsCode || "",
        })),
      };

      const confirmacion = await Swal.fire({
        ...swalBase,
        icon: "question",
        title: "Confirmar devolución",
        text: `¿Desea crear la devolución de la entrada ${formDataDevolucion.DocNum || ""}?`,
        showCancelButton: true,
        confirmButtonText: "Sí, devolver",
        cancelButtonText: "Cancelar",
      });

      if (!confirmacion.isConfirmed) return;

      Swal.fire({
        ...swalBase,
        title: "Creando devolución de mercancía",
        text: "Por favor espere...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const res = await fetch(API_URL_CREAR_DEVOLUCION_MERCANCIA, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "API-Key": API_KEY,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      Swal.close();

      if (!res.ok || data.answer !== "true") {
        throw new Error(
          data.message || "No fue posible crear la devolución de mercancía."
        );
      }

      await Swal.fire({
        ...swalBase,
        icon: "success",
        title: "Devolución exitosa",
        text: data.message || "Devolución de mercancía creada correctamente.",
        confirmButtonText: "Imprimir",
      });

      await imprimirEntradaTermicaSecuencial({
        logoUrl: LOGO_TICKET,
        docNum: formDataDevolucion.DocNum || "",
        formData: {
          ...formDataDevolucion,
          DocumentLines: lineasConCantidad,
          Comments: comentariosFinales,
        },
        copyLabel: "DEVOLUCIÓN",
      });

      closeModalDevolucion();
      fetchEntradasMercanciaDevolucion();
      fetchEntradasMercanciaCancelacion();
      fetchOrdenes();
    } catch (err) {
      console.error(err);
      Swal.close();

      Swal.fire({
        ...swalBase,
        icon: "error",
        title: "Error al crear devolución",
        text: err.message || "Ocurrió un error al crear la devolución de mercancía.",
      });
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setDetalleOrden(null);
    setFormData(null);
    setErrorDetalle("");
  };

  return (
    <div className="entrada-mercancia-container">
      <header className="entrada-mercancia-header">
        <div>
          <h1>Órdenes de Compra</h1>
          <p>Seleccione una orden de compra abierta</p>
        </div>
      </header>

      {!loading && !error && (
        <>
          <section className="orden-compra-combo-section">
            <div className="orden-compra-combo-wrapper" ref={ordenSearchRef}>
              <label className="orden-compra-label">
                Buscar Orden de Compra para ingreso
              </label>

              <input
                type="text"
                className="orden-compra-input"
                autoComplete="off"
                placeholder="Buscar por Proveedor o # Documento..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setShowOptions(true);
                }}
                onFocus={() => setShowOptions(true)}
              />

              {showOptions && (
                <div className="orden-compra-options">
                  {filteredOrdenes.length > 0 ? (
                    filteredOrdenes.map((orden) => (
                      <div
                        key={orden.DocEntry}
                        className="orden-compra-option"
                        onClick={() => handleSelectOrden(orden)}
                      >
                        {orden.DocNum_CardName || `${orden.DocNum} - ${orden.CardName}`}
                      </div>
                    ))
                  ) : (
                    <div className="orden-compra-option-empty">
                      No se encontraron resultados
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>

          <section className="orden-compra-combo-section">
            <div className="orden-compra-combo-wrapper" ref={cancelacionSearchRef}>
              <label className="orden-compra-label">
                Buscar Entrada de Mercancía para cancelación
              </label>

              <input
                type="text"
                className="orden-compra-input"
                autoComplete="off"
                placeholder="Buscar entrada por Proveedor o # Documento..."
                value={searchCancelacion}
                onChange={(e) => {
                  setSearchCancelacion(e.target.value);
                  setShowOptionsCancelacion(true);
                }}
                onFocus={() => setShowOptionsCancelacion(true)}
              />

              {showOptionsCancelacion && (
                <div className="orden-compra-options">
                  {filteredEntradasCancelacion.length > 0 ? (
                    filteredEntradasCancelacion.map((entrada) => (
                      <div
                        key={entrada.DocEntry}
                        className="orden-compra-option"
                        onClick={() => handleSelectEntradaCancelacion(entrada)}
                      >
                        {entrada.DocNum_CardName ||
                          `${entrada.DocNum} - ${entrada.CardName}`}
                      </div>
                    ))
                  ) : (
                    <div className="orden-compra-option-empty">
                      No se encontraron entradas de mercancía
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>

          <section className="orden-compra-combo-section">
            <div className="orden-compra-combo-wrapper" ref={devolucionSearchRef}>
              <label className="orden-compra-label">
                Buscar Entrada de Mercancía para devolución
              </label>

              <input
                type="text"
                className="orden-compra-input"
                autoComplete="off"
                placeholder="Buscar entrada por Proveedor o # Documento..."
                value={searchDevolucion}
                onChange={(e) => {
                  setSearchDevolucion(e.target.value);
                  setShowOptionsDevolucion(true);
                }}
                onFocus={() => setShowOptionsDevolucion(true)}
              />

              {showOptionsDevolucion && (
                <div className="orden-compra-options">
                  {filteredEntradasDevolucion.length > 0 ? (
                    filteredEntradasDevolucion.map((entrada) => (
                      <div
                        key={entrada.DocEntry}
                        className="orden-compra-option"
                        onClick={() => handleSelectEntradaDevolucion(entrada)}
                      >
                        {entrada.DocNum_CardName ||
                          `${entrada.DocNum} - ${entrada.CardName}`}
                      </div>
                    ))
                  ) : (
                    <div className="orden-compra-option-empty">
                      No se encontraron entradas de mercancía
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        </>
      )}

      {modalOpen && formData && (
        <div className="modal-overlay" onClick={closeModal}>
          <div
            className="modal-content modal-orden-compra"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div className="modal-header-text">
                <h2>Detalle Orden de Compra</h2>
                <p className="modal-subtitle">
                  DocNum: {formData.DocNum} | Proveedor: {formData.CardName}
                </p>
              </div>

              <button className="modal-close-btn" onClick={closeModal}>
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="modal-section">
                <h3 className="modal-section-title">Datos de encabezado</h3>

                <div className="orden-header-grid">
                  <div className="form-field form-field-sm">
                    <label className="form-label"># Documento</label>
                    <div className="form-value form-readonly">{formData.DocNum}</div>
                  </div>

                  <div className="form-field form-field-md">
                    <label className="form-label">Codigo Proveedor</label>
                    <div className="form-value form-readonly">{formData.CardCode}</div>
                  </div>

                  <div className="form-field form-field-xl">
                    <label className="form-label">Nombre Proveedor</label>
                    <div className="form-value form-readonly">{formData.CardName}</div>
                  </div>

                  <div className="form-field form-field-md">
                    <label className="form-label"># Referencia</label>
                    <input
                      className="form-input"
                      type="text"
                      autoComplete="off"
                      value={formData.NumAtCard}
                      onChange={(e) => handleHeaderChange("NumAtCard", e.target.value)}
                    />
                  </div>

                  <div className="form-field form-field-sm">
                    <label className="form-label">Fecha de Contabilización</label>
                    <input
                      className="form-date"
                      type="date"
                      value={formData.TaxDate}
                      onChange={(e) => handleHeaderChange("TaxDate", e.target.value)}
                    />
                  </div>

                  <div className="form-field form-field-sm">
                    <label className="form-label">Fecha de Vencimiento</label>
                    <input
                      className="form-date"
                      type="date"
                      value={formData.DocDueDate}
                      onChange={(e) => handleHeaderChange("DocDueDate", e.target.value)}
                    />
                  </div>

                  <div className="form-field form-field-sm">
                    <label className="form-label">Fecha de Documento</label>
                    <input
                      className="form-date"
                      type="date"
                      value={formData.DocDate}
                      onChange={(e) => handleHeaderChange("DocDate", e.target.value)}
                    />
                  </div>

                  <div className="form-field form-field-md">
                    <label className="form-label">Nit Factura</label>
                    <input
                      className="form-input"
                      type="text"
                      autoComplete="off"
                      value={formData.U_FacNit}
                      onChange={(e) => handleHeaderChange("U_FacNit", e.target.value)}
                    />
                  </div>

                  <div className="form-field form-field-xl">
                    <label className="form-label">Nombre Factura</label>
                    <input
                      className="form-input"
                      type="text"
                      autoComplete="off"
                      value={formData.U_FacNom}
                      onChange={(e) => handleHeaderChange("U_FacNom", e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <h3 className="modal-section-title">Líneas de la orden</h3>

                <div className="lines-table-wrapper">
                  <div className="lines-table">
                    <div className="lines-header">
                      <span className="col-numero-linea">No.</span>
                      <span className="col-accion">Acción</span>
                      <span>Código</span>
                      <span className="col-descripcion">Descripción</span>
                      <span>Cantidad</span>
                      <span>Pendiente</span>
                      <span>Almacén</span>
                      <span>Precio Info</span>
                      <span>Precio con IVA</span>
                      <span>Total línea</span>
                    </div>

                    {Array.isArray(formData.DocumentLines) &&
                    formData.DocumentLines.length > 0 ? (
                      formData.DocumentLines.map((linea, index) => {
                        const isClosed =
                          linea.IsClosedLine || Number(linea.OpenQuantity || 0) <= 0;

                        const totalLinea = round2(
                          Number(linea.Quantity || 0) * Number(linea.PrecioInfo || 0)
                        );

                        return (
                          <div
                            key={index}
                            className={`lines-row ${isClosed ? "lines-row-closed" : ""}`}
                          >
                            <span className="line-text line-numero">{index + 1}</span>

                            <div className="cell-accion">
                              <button
                                type="button"
                                className="btn-eliminar-linea"
                                onClick={() => handleEliminarLinea(index)}
                              >
                                Eliminar
                              </button>
                            </div>

                            <span className="line-text">{linea.ItemCode || "-"}</span>

                            <span className="line-text cell-descripcion">
                              {linea.ItemDescription || linea.Dscription || "-"}
                            </span>

                            <input
                              className={`line-input ${isClosed ? "line-input-closed" : ""}`}
                              type="text"
                              inputMode="decimal"
                              autoComplete="off"
                              value={getEditableDecimalValue(linea, "Quantity")}
                              data-caret-key={`line-${index}-Quantity`}
                              disabled={isClosed}
                              onFocus={(e) => e.target.select()}
                              onChange={(e) =>
                                handleLineChange(index, "Quantity", e.target.value, e)
                              }
                              onBlur={() => handleLineInputBlur(index, "Quantity")}
                            />

                            <span className="line-text">
                              {formatMoney2(
                                linea.OpenQuantity != null ? linea.OpenQuantity : 0
                              )}
                            </span>

                            <select
                              className={`line-input ${isClosed ? "line-input-closed" : ""}`}
                              value={linea.WarehouseCode || linea.WhsCode || ""}
                              disabled={isClosed}
                              onChange={(e) =>
                                handleLineChange(index, "WarehouseCode", e.target.value)
                              }
                            >
                              <option value="">Seleccione</option>
                              {almacenes.map((almacen) => (
                                <option key={almacen.WhsCode} value={almacen.WhsCode}>
                                  {almacen.WhsCode}
                                </option>
                              ))}
                            </select>

                            <input
                              className={`line-input ${isClosed ? "line-input-closed" : ""}`}
                              type="text"
                              inputMode="decimal"
                              autoComplete="off"
                              value={getEditableDecimalValue(linea, "PrecioInfo")}
                              data-caret-key={`line-${index}-PrecioInfo`}
                              disabled={isClosed}
                              onFocus={(e) => e.target.select()}
                              onChange={(e) =>
                                handleLineChange(index, "PrecioInfo", e.target.value, e)
                              }
                              onBlur={() => handleLineInputBlur(index, "PrecioInfo")}
                            />

                            <span className="line-text">
                              {formatMoney4(linea.PrecioConIVA || 0)}
                            </span>

                            <span className="line-total">
                              {formatMoney2(totalLinea)}
                            </span>
                          </div>
                        );
                      })
                    ) : (
                      <div className="lines-row">
                        <span>-</span>
                        <span>-</span>
                        <span>-</span>
                        <span>No hay líneas para mostrar</span>
                        <span>-</span>
                        <span>-</span>
                        <span>-</span>
                        <span>-</span>
                        <span>-</span>
                        <span>-</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <div className="orden-footer-grid">
                  <div className="form-field form-field-comments">
                    <label className="form-label">Comentario</label>
                    <textarea
                      className="form-textarea"
                      autoComplete="off"
                      autoCorrect="off"
                      value={comentarioCompleto}
                      onChange={(e) => handleCommentsChange(e.target.value)}
                    />
                  </div>

                  <div className="form-field form-field-total">
                    <label className="form-label">Total antes del descuento</label>
                    <div className="form-value form-readonly total-documento">
                      {formatMoney2(formData.TotalAntesDescuento || 0)}
                    </div>

                    <label className="form-label" style={{ marginTop: "12px" }}>
                      Impuesto
                    </label>
                    <div className="form-value form-readonly total-documento">
                      {formatMoney2(formData.Impuesto || 0)}
                    </div>

                    <label className="form-label" style={{ marginTop: "12px" }}>
                      Total del documento
                    </label>
                    <input
                      className="form-input total-documento-encabezado"
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      value={formData.DocTotalInput}
                      data-caret-key="doc-total"
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => handleDocTotalInputChange(e.target.value, e)}
                      onBlur={() => {
                        const valor = parseDecimal(formData.DocTotalInput);
                        setFormData((prev) =>
                          prev
                            ? {
                                ...prev,
                                DocTotalInput: valor > 0 ? formatMoney2(valor) : "",
                              }
                            : prev
                        );
                      }}
                    />

                    <small className="form-help-text">
                      Total esperado según líneas actuales: {" "}
                      {formatMoney2(formData.ValidationBaseDocTotal || 0)} | Rango
                      permitido: ± Q{MAX_DIFERENCIA_TOTAL.toFixed(2)}
                    </small>
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  Cancelar
                </button>
                <button type="button" className="btn-primary" onClick={handleGuardar}>
                  Realizar Ingreso
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {modalCancelacionOpen && formDataCancelacion && (
        <div className="modal-overlay" onClick={closeModalCancelacion}>
          <div
            className="modal-content modal-orden-compra"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div className="modal-header-text">
                <h2>Detalle Entrada de Mercancía</h2>
                <p className="modal-subtitle">
                  DocNum: {formDataCancelacion.DocNum} | Proveedor:{" "}
                  {formDataCancelacion.CardName}
                </p>
              </div>

              <button className="modal-close-btn" onClick={closeModalCancelacion}>
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="modal-section">
                <h3 className="modal-section-title">Datos de encabezado</h3>

                <div className="orden-header-grid">
                  <div className="form-field form-field-sm">
                    <label className="form-label"># Documento</label>
                    <div className="form-value form-readonly">
                      {formDataCancelacion.DocNum}
                    </div>
                  </div>

                  <div className="form-field form-field-md">
                    <label className="form-label">Codigo Proveedor</label>
                    <div className="form-value form-readonly">
                      {formDataCancelacion.CardCode}
                    </div>
                  </div>

                  <div className="form-field form-field-xl">
                    <label className="form-label">Nombre Proveedor</label>
                    <div className="form-value form-readonly">
                      {formDataCancelacion.CardName}
                    </div>
                  </div>

                  <div className="form-field form-field-md">
                    <label className="form-label"># Referencia</label>
                    <input
                      className="form-input"
                      type="text"
                      value={formDataCancelacion.NumAtCard}
                      disabled
                      readOnly
                    />
                  </div>

                  <div className="form-field form-field-sm">
                    <label className="form-label">Fecha de Contabilización</label>
                    <input
                      className="form-date"
                      type="date"
                      value={formDataCancelacion.TaxDate}
                      disabled
                      readOnly
                    />
                  </div>

                  <div className="form-field form-field-sm">
                    <label className="form-label">Fecha de Vencimiento</label>
                    <input
                      className="form-date"
                      type="date"
                      value={formDataCancelacion.DocDueDate}
                      disabled
                      readOnly
                    />
                  </div>

                  <div className="form-field form-field-sm">
                    <label className="form-label">Fecha de Documento</label>
                    <input
                      className="form-date"
                      type="date"
                      value={formDataCancelacion.DocDate}
                      disabled
                      readOnly
                    />
                  </div>

                  <div className="form-field form-field-md">
                    <label className="form-label">Nit Factura</label>
                    <input
                      className="form-input"
                      type="text"
                      value={formDataCancelacion.U_FacNit}
                      disabled
                      readOnly
                    />
                  </div>

                  <div className="form-field form-field-xl">
                    <label className="form-label">Nombre Factura</label>
                    <input
                      className="form-input"
                      type="text"
                      value={formDataCancelacion.U_FacNom}
                      disabled
                      readOnly
                    />
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <h3 className="modal-section-title">Líneas de la entrada</h3>

                <div className="lines-table-wrapper">
                  <div className="lines-table">
                    <div className="lines-header">
                      <span className="col-numero-linea">No.</span>
                      <span className="col-accion">Acción</span>
                      <span>Código</span>
                      <span className="col-descripcion">Descripción</span>
                      <span>Cantidad</span>
                      <span>Pendiente</span>
                      <span>Almacén</span>
                      <span>Precio Info</span>
                      <span>Precio con IVA</span>
                      <span>Total línea</span>
                    </div>

                    {Array.isArray(formDataCancelacion.DocumentLines) &&
                    formDataCancelacion.DocumentLines.length > 0 ? (
                      formDataCancelacion.DocumentLines.map((linea, index) => {
                        const totalLinea = round2(
                          Number(linea.Quantity || 0) *
                            Number(linea.PrecioInfo || 0)
                        );

                        return (
                          <div key={index} className="lines-row">
                            <span className="line-text line-numero">{index + 1}</span>

                            <div className="cell-accion">
                              <span className="line-text">-</span>
                            </div>

                            <span className="line-text">{linea.ItemCode || "-"}</span>

                            <span className="line-text cell-descripcion">
                              {linea.ItemDescription || linea.Dscription || "-"}
                            </span>

                            <input
                              className="line-input line-input-closed"
                              type="text"
                              value={formatMoney2(linea.Quantity || 0)}
                              disabled
                              readOnly
                            />

                            <span className="line-text">
                              {formatMoney2(
                                linea.OpenQuantity != null
                                  ? linea.OpenQuantity
                                  : linea.Quantity || 0
                              )}
                            </span>

                            <select
                              className="line-input line-input-closed"
                              value={linea.WarehouseCode || linea.WhsCode || ""}
                              disabled
                            >
                              <option value="">
                                {linea.WarehouseCode || linea.WhsCode || "Seleccione"}
                              </option>
                              {almacenes.map((almacen) => (
                                <option key={almacen.WhsCode} value={almacen.WhsCode}>
                                  {almacen.WhsCode}
                                </option>
                              ))}
                            </select>

                            <input
                              className="line-input line-input-closed"
                              type="text"
                              value={formatMoney4(linea.PrecioInfo || 0)}
                              disabled
                              readOnly
                            />

                            <span className="line-text">
                              {formatMoney4(linea.PrecioConIVA || 0)}
                            </span>

                            <span className="line-total">
                              {formatMoney2(totalLinea)}
                            </span>
                          </div>
                        );
                      })
                    ) : (
                      <div className="lines-row">
                        <span>-</span>
                        <span>-</span>
                        <span>-</span>
                        <span>No hay líneas para mostrar</span>
                        <span>-</span>
                        <span>-</span>
                        <span>-</span>
                        <span>-</span>
                        <span>-</span>
                        <span>-</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <div className="orden-footer-grid">
                  <div className="form-field form-field-comments">
                    <label className="form-label">Comentario</label>
                    <textarea
                      className="form-textarea"
                      autoComplete="off"
                      autoCorrect="off"
                      value={comentarioCompletoCancelacion}
                      onChange={(e) =>
                        handleCommentsCancelacionChange(e.target.value)
                      }
                    />
                  </div>

                  <div className="form-field form-field-total">
                    <label className="form-label">Total antes del descuento</label>
                    <div className="form-value form-readonly total-documento">
                      {formatMoney2(formDataCancelacion.TotalAntesDescuento || 0)}
                    </div>

                    <label className="form-label" style={{ marginTop: "12px" }}>
                      Impuesto
                    </label>
                    <div className="form-value form-readonly total-documento">
                      {formatMoney2(formDataCancelacion.Impuesto || 0)}
                    </div>

                    <label className="form-label" style={{ marginTop: "12px" }}>
                      Total del documento
                    </label>
                    <input
                      className="form-input total-documento-encabezado"
                      type="text"
                      value={formDataCancelacion.DocTotalInput}
                      disabled
                      readOnly
                    />
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModalCancelacion}
                >
                  Cerrar
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleCancelarEntradaMercancia}
                >
                  Cancelar Entrada
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {modalDevolucionOpen && formDataDevolucion && (
        <div className="modal-overlay" onClick={closeModalDevolucion}>
          <div
            className="modal-content modal-orden-compra"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div className="modal-header-text">
                <h2>Detalle Entrada de Mercancía para Devolución</h2>
                <p className="modal-subtitle">
                  DocNum: {formDataDevolucion.DocNum} | Proveedor:{" "}
                  {formDataDevolucion.CardName}
                </p>
              </div>

              <button className="modal-close-btn" onClick={closeModalDevolucion}>
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="modal-section">
                <h3 className="modal-section-title">Datos de encabezado</h3>

                <div className="orden-header-grid">
                  <div className="form-field form-field-sm">
                    <label className="form-label"># Documento</label>
                    <div className="form-value form-readonly">
                      {formDataDevolucion.DocNum}
                    </div>
                  </div>

                  <div className="form-field form-field-md">
                    <label className="form-label">Codigo Proveedor</label>
                    <div className="form-value form-readonly">
                      {formDataDevolucion.CardCode}
                    </div>
                  </div>

                  <div className="form-field form-field-xl">
                    <label className="form-label">Nombre Proveedor</label>
                    <div className="form-value form-readonly">
                      {formDataDevolucion.CardName}
                    </div>
                  </div>

                  <div className="form-field form-field-md">
                    <label className="form-label"># Referencia</label>
                    <input
                      className="form-input"
                      type="text"
                      value={formDataDevolucion.NumAtCard}
                      disabled
                      readOnly
                    />
                  </div>

                  <div className="form-field form-field-sm">
                    <label className="form-label">Fecha de Contabilización</label>
                    <input
                      className="form-date"
                      type="date"
                      value={formDataDevolucion.TaxDate}
                      disabled
                      readOnly
                    />
                  </div>

                  <div className="form-field form-field-sm">
                    <label className="form-label">Fecha de Vencimiento</label>
                    <input
                      className="form-date"
                      type="date"
                      value={formDataDevolucion.DocDueDate}
                      disabled
                      readOnly
                    />
                  </div>

                  <div className="form-field form-field-sm">
                    <label className="form-label">Fecha de Documento</label>
                    <input
                      className="form-date"
                      type="date"
                      value={formDataDevolucion.DocDate}
                      disabled
                      readOnly
                    />
                  </div>

                  <div className="form-field form-field-md">
                    <label className="form-label">Nit Factura</label>
                    <input
                      className="form-input"
                      type="text"
                      value={formDataDevolucion.U_FacNit}
                      disabled
                      readOnly
                    />
                  </div>

                  <div className="form-field form-field-xl">
                    <label className="form-label">Nombre Factura</label>
                    <input
                      className="form-input"
                      type="text"
                      value={formDataDevolucion.U_FacNom}
                      disabled
                      readOnly
                    />
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <h3 className="modal-section-title">Líneas de la entrada</h3>

                <div className="lines-table-wrapper">
                  <div className="lines-table">
                    <div className="lines-header">
                      <span className="col-numero-linea">No.</span>
                      <span className="col-accion">Acción</span>
                      <span>Código</span>
                      <span className="col-descripcion">Descripción</span>
                      <span>Cantidad</span>
                      <span>Pendiente</span>
                      <span>Almacén</span>
                      <span>Precio Info</span>
                      <span>Precio con IVA</span>
                      <span>Total línea</span>
                    </div>

                    {Array.isArray(formDataDevolucion.DocumentLines) &&
                    formDataDevolucion.DocumentLines.length > 0 ? (
                      formDataDevolucion.DocumentLines.map((linea, index) => {
                        const totalLinea = round2(
                          Number(linea.Quantity || 0) *
                            Number(linea.PrecioInfo || 0)
                        );

                        return (
                          <div key={index} className="lines-row">
                            <span className="line-text line-numero">{index + 1}</span>

                            <div className="cell-accion">
                              <span className="line-text">-</span>
                            </div>

                            <span className="line-text">{linea.ItemCode || "-"}</span>

                            <span className="line-text cell-descripcion">
                              {linea.ItemDescription || linea.Dscription || "-"}
                            </span>

                            <input
                              className="line-input line-input-closed"
                              type="text"
                              value={formatMoney2(linea.Quantity || 0)}
                              disabled
                              readOnly
                            />

                            <span className="line-text">
                              {formatMoney2(
                                linea.OpenQuantity != null
                                  ? linea.OpenQuantity
                                  : linea.Quantity || 0
                              )}
                            </span>

                            <select
                              className="line-input line-input-closed"
                              value={linea.WarehouseCode || linea.WhsCode || ""}
                              disabled
                            >
                              <option value="">
                                {linea.WarehouseCode || linea.WhsCode || "Seleccione"}
                              </option>
                              {almacenes.map((almacen) => (
                                <option key={almacen.WhsCode} value={almacen.WhsCode}>
                                  {almacen.WhsCode}
                                </option>
                              ))}
                            </select>

                            <input
                              className="line-input line-input-closed"
                              type="text"
                              value={formatMoney4(linea.PrecioInfo || 0)}
                              disabled
                              readOnly
                            />

                            <span className="line-text">
                              {formatMoney4(linea.PrecioConIVA || 0)}
                            </span>

                            <span className="line-total">
                              {formatMoney2(totalLinea)}
                            </span>
                          </div>
                        );
                      })
                    ) : (
                      <div className="lines-row">
                        <span>-</span>
                        <span>-</span>
                        <span>-</span>
                        <span>No hay líneas para mostrar</span>
                        <span>-</span>
                        <span>-</span>
                        <span>-</span>
                        <span>-</span>
                        <span>-</span>
                        <span>-</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <div className="orden-footer-grid">
                  <div className="form-field form-field-comments">
                    <label className="form-label">Comentario</label>
                    <textarea
                      className="form-textarea"
                      autoComplete="off"
                      autoCorrect="off"
                      value={comentarioCompletoDevolucion}
                      onChange={(e) =>
                        handleCommentsDevolucionChange(e.target.value)
                      }
                    />
                  </div>

                  <div className="form-field form-field-total">
                    <label className="form-label">Total antes del descuento</label>
                    <div className="form-value form-readonly total-documento">
                      {formatMoney2(formDataDevolucion.TotalAntesDescuento || 0)}
                    </div>

                    <label className="form-label" style={{ marginTop: "12px" }}>
                      Impuesto
                    </label>
                    <div className="form-value form-readonly total-documento">
                      {formatMoney2(formDataDevolucion.Impuesto || 0)}
                    </div>

                    <label className="form-label" style={{ marginTop: "12px" }}>
                      Total del documento
                    </label>
                    <input
                      className="form-input total-documento-encabezado"
                      type="text"
                      value={formDataDevolucion.DocTotalInput}
                      disabled
                      readOnly
                    />
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModalDevolucion}
                >
                  Cerrar
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleCrearDevolucionMercancia}
                >
                  Crear Devolución
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default EntradaMercancias;